import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Alert, Badge, Button, Card, Field, Input, Select } from '../ui';
import { api, ApiError } from '../../lib/api';
import { uploadWithProgress } from '../../lib/upload';
import { DOC_KINDS, type DocKind, type Document } from '../../lib/types';
import { TagInput } from './TagInput';

const statusTone = (s: string) =>
  (s === 'pass'
    ? 'green'
    : s === 'not_compliant' || s === 'failed'
      ? 'red'
      : s === 'verifying'
        ? 'blue'
        : 'slate') as 'green' | 'red' | 'blue' | 'slate';

export function DocumentsTab({
  orgId,
  pid,
  canUpload,
  onChanged,
}: {
  orgId: string;
  pid: string;
  canUpload: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const base = `/api/v1/orgs/${orgId}/products/${pid}/documents`;
  const [docs, setDocs] = useState<Document[] | null>(null);
  const [msg, setMsg] = useState<{ tone: 'red' | 'green'; text: string } | null>(null);
  const [form, setForm] = useState<{
    file: File | null;
    name: string;
    kind: DocKind | '';
    tags: string[];
    supersedesDocId: string;
  }>({ file: null, name: '', kind: '', tags: [], supersedesDocId: '' });
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [editTags, setEditTags] = useState<{ id: string; tags: string[] } | null>(null);
  const [pct, setPct] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const load = useCallback(
    () => api<{ documents: Document[] }>(base).then((r) => setDocs(r.documents)),
    [base],
  );
  useEffect(() => {
    void load();
  }, [load]);
  // poll while VaultSage is still converting
  useEffect(() => {
    if (!docs?.some((d) => d.vaultsageProcessing === 'pending' || d.vaultsageProcessing === 'processing'))
      return;
    const id = setTimeout(() => void load(), 4000);
    return () => clearTimeout(id);
  }, [docs, load]);

  const fail = (e: unknown) =>
    setMsg({
      tone: 'red',
      text:
        e instanceof ApiError
          ? t([`docs.err_${e.code}`, 'docs.err_generic'], { code: e.code }) +
            (e.code === 'UNSUPPORTED_FILE_TYPE' || e.code === 'INVALID_FORMULATION' ? ` ${e.message}` : '')
          : String(e),
    });
  const upload = async () => {
    if (!form.file) return;
    setMsg(null);
    setPct(0);
    try {
      const fields: Record<string, string> = {
        name: form.name,
        kind: form.kind,
        tags: form.tags.join(','),
        supersedesDocId: form.supersedesDocId,
      };
      const d = await uploadWithProgress<Document>(base, form.file, fields, setPct);
      setMsg({ tone: 'green', text: `${d.id} - ${t('docs.status_uploaded')}` });
      setForm({ file: null, name: '', kind: '', tags: [], supersedesDocId: '' });
      if (fileRef.current) fileRef.current.value = '';
      await load();
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setPct(null);
    }
  };
  const saveTags = async () => {
    if (!editTags) return;
    try {
      await api(`${base}/${editTags.id}`, { method: 'PATCH', json: { tags: editTags.tags } });
      setEditTags(null);
      await load();
    } catch (e) {
      fail(e);
    }
  };
  const [busyId, setBusyId] = useState<string | null>(null);
  /** soft exclude / include (the document is kept; only the verification counts change) */
  const toggleExcluded = async (d: Document) => {
    setMsg(null);
    setBusyId(d.id);
    try {
      await api(`${base}/${d.id}/exclude`, { method: 'POST', json: { excluded: !d.excluded } });
      await load();
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  };
  const allTags = useMemo(
    () => [...new Set((docs ?? []).flatMap((d) => d.tags ?? []))].sort((a, b) => a.localeCompare(b)),
    [docs],
  );
  // verification summary over current versions (superseded versions are history); excluded documents are not counted
  const summary = useMemo(() => {
    const all = (docs ?? []).filter((d) => !d.supersededByDocId);
    const cur = all.filter((d) => !d.excluded);
    const n = (st: string[]) => cur.filter((d) => st.includes(d.status)).length;
    return {
      total: cur.length,
      excluded: all.length - cur.length,
      verified: n(['pass', 'not_compliant']),
      pass: n(['pass']),
      notCompliant: n(['not_compliant']),
      verifying: n(['verifying']),
      notVerified: n(['uploaded', 'failed']),
    };
  }, [docs]);
  const shown = (docs ?? []).filter((d) => !tagFilter || (d.tags ?? []).includes(tagFilter));
  return (
    <div className="space-y-4">
      {canUpload && (
        <Card
          title={
            form.supersedesDocId
              ? `${t('docs.newVersion')} (${t('docs.supersedes', { id: form.supersedesDocId })})`
              : t('docs.upload')
          }
        >
          <div className="grid min-w-0 gap-3 md:grid-cols-[1.4fr_1fr_0.8fr_1.2fr]">
            <Field label={t('docs.upload')} hint={t('docs.dropHint')}>
              <input
                ref={fileRef}
                type="file"
                className="block w-full min-w-0 text-sm file:mr-2 file:rounded-md file:border file:border-slate-300 file:bg-white file:px-2 file:py-1 file:text-sm"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,.xlsx,.xls,.docx,.doc,.pptx,.hwp,.hwpx"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setForm((s) => ({
                    ...s,
                    file: f,
                    name: s.name || (f ? f.name.replace(/\.[^.]+$/, '') : ''),
                  }));
                }}
              />
            </Field>
            <Field label={t('docs.nameLabel')}>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                maxLength={80}
              />
            </Field>
            <Field label={t('docs.kind')}>
              <Select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as DocKind | '' })}
              >
                <option value="">auto</option>
                {DOC_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`docs.kind_${k}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('docs.tags')} hint={t('docs.tagsHint')}>
              <TagInput
                value={form.tags}
                onChange={(tags) => setForm((f) => ({ ...f, tags }))}
                suggestions={allTags}
                placeholder={t('docs.tagsPlaceholder')}
                invalidHint={t('docs.err_INVALID_TAG')}
              />
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button onClick={() => void upload()} disabled={!form.file || pct !== null}>
              {pct !== null ? t('docs.uploading', { pct }) : t('docs.upload')}
            </Button>
            {form.supersedesDocId && (
              <Button variant="secondary" onClick={() => setForm({ ...form, supersedesDocId: '' })}>
                {t('products.cancel')}
              </Button>
            )}
            {pct !== null && (
              <div className="h-2 flex-1 rounded bg-slate-200">
                <div className="h-2 rounded bg-emerald-500" style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
        </Card>
      )}
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      {summary.total + summary.excluded > 0 && (
        <div className="grid gap-2 sm:grid-cols-6" aria-label={t('docs.summary')}>
          {(
            [
              ['verified', `${summary.verified} / ${summary.total}`, 'border-slate-300 text-slate-900'],
              ['pass', summary.pass, 'border-emerald-300 text-emerald-800'],
              ['not_compliant', summary.notCompliant, 'border-red-300 text-red-800'],
              ['verifying', summary.verifying, 'border-blue-300 text-blue-800'],
              ['not_verified', summary.notVerified, 'border-slate-200 text-slate-600'],
              ['excluded', summary.excluded, 'border-dashed border-slate-300 text-slate-500'],
            ] as const
          ).map(([k, v, cls]) => (
            <div key={k} className={`rounded-lg border bg-white px-3 py-2 ${cls}`}>
              <div className="text-xs uppercase tracking-wide opacity-80">{t(`docs.sum_${k}`)}</div>
              <div className="text-xl font-semibold">{v}</div>
            </div>
          ))}
        </div>
      )}
      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <span className="text-slate-500">{t('docs.filterByTag')}:</span>
          <button
            className={`rounded-full px-2 py-0.5 ${!tagFilter ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700'}`}
            onClick={() => setTagFilter(null)}
          >
            {t('review.all')}
          </button>
          {allTags.map((tg) => (
            <button
              key={tg}
              className={`rounded-full px-2 py-0.5 ${tagFilter === tg ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-800'}`}
              onClick={() => setTagFilter(tagFilter === tg ? null : tg)}
            >
              #{tg}
            </button>
          ))}
        </div>
      )}
      <Card>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2">{t('docs.id')}</th>
              <th>{t('docs.nameLabel')}</th>
              <th>{t('docs.kind')}</th>
              <th>{t('docs.tags')}</th>
              <th>{t('docs.status')}</th>
              <th>{t('docs.processing')}</th>
              <th>{t('docs.uploadedBy')}</th>
              <th className="text-right">{t('members.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.map((d) => (
              <tr key={d.id} className={d.supersededByDocId || d.excluded ? 'bg-slate-50 opacity-60' : ''}>
                <td className="py-2 font-mono text-xs">
                  <Link className="text-emerald-700 underline" to={`/products/${pid}/documents/${d.id}`}>
                    {d.id}
                  </Link>
                </td>
                <td>
                  {d.name}
                  <span className="ml-1 text-xs text-slate-400">{d.ext}</span>
                  {d.supersedesDocId && (
                    <div className="text-xs text-slate-500">
                      {t('docs.supersedes', { id: d.supersedesDocId })}
                    </div>
                  )}
                </td>
                <td>
                  <Badge>{t(`docs.kind_${d.kind}`)}</Badge>
                </td>
                <td className="max-w-[14rem] text-xs">
                  {editTags?.id === d.id ? (
                    <div className="space-y-1">
                      <TagInput
                        value={editTags.tags}
                        onChange={(tags) => setEditTags({ id: d.id, tags })}
                        suggestions={allTags}
                        invalidHint={t('docs.err_INVALID_TAG')}
                      />
                      <div className="flex gap-1">
                        <Button className="text-xs" onClick={() => void saveTags()}>
                          {t('products.save')}
                        </Button>
                        <Button variant="ghost" className="text-xs" onClick={() => setEditTags(null)}>
                          {t('products.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1">
                      {(d.tags ?? []).map((tg) => (
                        <button
                          key={tg}
                          className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800 hover:bg-emerald-100"
                          onClick={() => setTagFilter(tg)}
                        >
                          #{tg}
                        </button>
                      ))}
                      {canUpload && (
                        <button
                          className="text-slate-400 hover:text-emerald-700"
                          title={t('docs.editTags')}
                          onClick={() => setEditTags({ id: d.id, tags: d.tags ?? [] })}
                        >
                          {(d.tags ?? []).length ? '✎' : `+ ${t('docs.tag')}`}
                        </button>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <Badge tone={statusTone(d.status)}>{t(`docs.status_${d.status}`)}</Badge>
                  {d.latestCheckId && (
                    <div className="text-[10px] text-slate-500">
                      {(d.latestResult?.jurisdictions ?? ['us-fda'])
                        .map((j) => (j === 'kr-mfds' ? 'MFDS(KR)' : 'FDA(US)'))
                        .join(' + ')}
                    </div>
                  )}
                  {d.excluded && (
                    <span
                      className="ml-1"
                      title={t('docs.excludedBy', {
                        who: d.excludedByEmail ?? '',
                        date: (d.excludedAt ?? '').slice(0, 10),
                      })}
                    >
                      <Badge tone="amber">{t('docs.excluded')}</Badge>
                    </span>
                  )}
                </td>
                <td className="text-xs text-slate-500">{d.vaultsageProcessing}</td>
                <td className="text-xs">
                  {d.uploadedByEmail}
                  <div className="text-slate-400">{d.uploadedAt.slice(0, 16).replace('T', ' ')}</div>
                </td>
                <td className="text-right">
                  <div className="flex justify-end gap-1">
                    <a
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      href={`${base}/${d.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('docs.open')}
                    </a>
                    <a
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      href={`${base}/${d.id}/file?download=1`}
                    >
                      {t('docs.download')}
                    </a>
                    {canUpload && (
                      <Button
                        variant="ghost"
                        className="text-xs"
                        disabled={busyId === d.id}
                        title={d.excluded ? t('docs.includeHint') : t('docs.excludeHint')}
                        onClick={() => void toggleExcluded(d)}
                      >
                        {d.excluded ? t('docs.include') : t('docs.exclude')}
                      </Button>
                    )}
                    {canUpload && !d.supersededByDocId && (
                      <Button
                        variant="secondary"
                        className="text-xs"
                        onClick={() =>
                          setForm({
                            ...form,
                            supersedesDocId: d.id,
                            kind: d.kind,
                            tags: d.tags ?? [],
                            name: d.name,
                          })
                        }
                      >
                        {t('docs.newVersion')}
                      </Button>
                    )}
                    {['label_artwork', 'label_copy', 'sfp'].includes(d.kind) ? (
                      <Link
                        to={`/products/${pid}/documents/${d.id}`}
                        className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                      >
                        {d.latestCheckId || d.status !== 'uploaded' ? t('review.title') : t('docs.verify')}
                      </Link>
                    ) : (
                      <Button
                        variant="secondary"
                        className="text-xs"
                        disabled
                        title={t('review.notReviewable')}
                      >
                        {t('docs.verify')}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {docs && shown.length === 0 && (
              <tr>
                <td colSpan={8} className="py-6 text-center text-slate-500">
                  {t('docs.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
